<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue"
import Swal from "sweetalert2"
import api from "@/api/axios.js"

// Clientes de Kommo que escribieron y no han recibido respuesta. Los casos los
// crea el workflow de n8n «Kommo - Observador de conversaciones» (el mismo que
// avisa por Telegram) y, cuando ve una respuesta en Kommo, los marca atendidos
// solo. Aquí el asesor puede tomarlos (en gestión) o cerrarlos a mano.

const ESTADOS = {
  pendiente: { label: "Pendiente", badge: "bg-warning text-dark" },
  en_gestion: { label: "En gestión", badge: "bg-info" },
  atendido: { label: "Atendido", badge: "bg-success" }
}

const FILTROS = [
  { valor: "embudo", label: "En el embudo" },
  { valor: "abiertos", label: "Sin atender" },
  { valor: "pendiente", label: "Pendientes" },
  { valor: "en_gestion", label: "En gestión" },
  { valor: "atendido", label: "Atendidos (7 días)" }
]

const ORIGENES = {
  waba: "WhatsApp",
  "com.amocrm.amocrmwa": "WhatsApp",
  instagram: "Instagram",
  instagram_business: "Instagram",
  facebook: "Messenger",
  telegram: "Telegram"
}

const casos = ref([])
const conteo = ref({ pendiente: 0, en_gestion: 0, atendidos_hoy: 0 })
const ultimaSync = ref(null)
const cargando = ref(true)
const filtro = ref("embudo")
// Pestaña "En el embudo": todos los leads del embudo vigilado, leídos de Kommo por el backend
const embudo = ref({ pipeline: null, leads: [], generadoAt: null })
const errorEmbudo = ref("")
const busqueda = ref("")
const ahora = ref(Date.now())
const guardando = ref(null)

let timerRecarga = null
let timerReloj = null
let timerBusqueda = null

async function cargarEmbudo({ refrescar = false } = {}) {
  try {
    const { data } = await api.get("/kommo/embudo", { params: refrescar ? { refrescar: 1 } : {} })
    embudo.value = data
    errorEmbudo.value = ""
  } catch (err) {
    errorEmbudo.value = err.response?.data?.error || "No se pudo consultar Kommo."
  }
}

async function cargar({ silencioso = false, refrescar = false } = {}) {
  if (!silencioso) cargando.value = true
  try {
    // En "En el embudo" solo se piden los contadores de casos; la lista sale de Kommo
    const enEmbudo = filtro.value === "embudo"
    const [{ data }] = await Promise.all([
      api.get("/kommo/pendientes", {
        params: enEmbudo ? { estado: "abiertos", limite: 1 } : { estado: filtro.value, q: busqueda.value || undefined }
      }),
      enEmbudo ? cargarEmbudo({ refrescar }) : null
    ])
    casos.value = data.pendientes
    conteo.value = data.conteo
    ultimaSync.value = data.ultimaSync
  } catch (err) {
    if (!silencioso) {
      Swal.fire({
        icon: "error",
        title: "No se pudieron cargar los clientes sin respuesta",
        text: err.response?.data?.error || "Error de conexión con el servidor."
      })
    }
  } finally {
    cargando.value = false
  }
}

function cambiarFiltro(valor) {
  filtro.value = valor
  cargar()
}

function buscar() {
  // En "En el embudo" la búsqueda se hace aquí mismo (embudoFiltrado)
  if (filtro.value === "embudo") return
  clearTimeout(timerBusqueda)
  timerBusqueda = setTimeout(() => cargar(), 350)
}

const embudoFiltrado = computed(() => {
  const q = busqueda.value.trim().toLowerCase()
  if (!q) return embudo.value.leads
  return embudo.value.leads.filter((l) =>
    [l.lead_nombre, l.contacto_nombre, l.telefono, l.email, l.responsable, l.etapa, String(l.lead_id)]
      .some((v) => String(v || "").toLowerCase().includes(q))
  )
})

function claseTiempoEmbudo(fecha) {
  const dias = (ahora.value - new Date(fecha).getTime()) / 86400000
  if (dias >= 7) return "text-danger fw-bold"
  if (dias >= 3) return "text-warning fw-bold"
  return "text-dark"
}

onMounted(() => {
  cargar()
  // Se refresca solo: n8n sincroniza cada 10 min, y otro asesor puede tomar un caso
  timerRecarga = setInterval(() => cargar({ silencioso: true }), 60 * 1000)
  timerReloj = setInterval(() => (ahora.value = Date.now()), 30 * 1000)
})

onBeforeUnmount(() => {
  clearInterval(timerRecarga)
  clearInterval(timerReloj)
  clearTimeout(timerBusqueda)
})

const totalSinAtender = computed(() => conteo.value.pendiente + conteo.value.en_gestion)

// Si n8n deja de sincronizar, el contador deja de ser confiable: se avisa
const syncAtrasada = computed(() => {
  if (!ultimaSync.value) return true
  return ahora.value - new Date(ultimaSync.value).getTime() > 30 * 60 * 1000
})

function tiempoDesde(fecha) {
  if (!fecha) return "—"
  const min = Math.max(0, Math.floor((ahora.value - new Date(fecha).getTime()) / 60000))
  if (min < 60) return `${min} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h} h ${min % 60} min`
  return `${Math.floor(h / 24)} d ${h % 24} h`
}

function horasEsperando(caso) {
  const fin = caso.estado === "atendido" && caso.atendido_at ? new Date(caso.atendido_at).getTime() : ahora.value
  return (fin - new Date(caso.cliente_escribio_at).getTime()) / 3600000
}

function claseEspera(caso) {
  if (caso.estado === "atendido") return "text-secondary"
  const h = horasEsperando(caso)
  if (h >= 12) return "text-danger fw-bold"
  if (h >= 6) return "text-warning fw-bold"
  return "text-dark"
}

function formatFecha(value) {
  if (!value) return ""
  return new Date(value).toLocaleString("es-CO", { dateStyle: "short", timeStyle: "short" })
}

function nombreOrigen(origen) {
  return ORIGENES[origen] || origen || "—"
}

function linkWhatsapp(telefono) {
  const digitos = String(telefono || "").replace(/\D/g, "")
  return digitos ? `https://wa.me/${digitos}` : null
}

const TEXTOS_CAMBIO = {
  en_gestion: { titulo: "¿Tomar este caso?", boton: "Sí, lo tomo", color: "#11cdef", ok: "Caso en gestión" },
  atendido: { titulo: "¿Marcar como atendido?", boton: "Sí, atendido", color: "#2dce89", ok: "Caso atendido" },
  pendiente: { titulo: "¿Devolver a pendiente?", boton: "Sí, devolver", color: "#fb6340", ok: "Caso devuelto a pendiente" }
}

async function cambiarEstado(caso, estado) {
  const t = TEXTOS_CAMBIO[estado]
  const { value: nota, isConfirmed } = await Swal.fire({
    icon: "question",
    title: t.titulo,
    text: caso.lead_nombre || caso.contacto_nombre || `Lead #${caso.lead_id}`,
    input: "textarea",
    inputLabel: "Nota (opcional)",
    showCancelButton: true,
    confirmButtonText: t.boton,
    cancelButtonText: "Cancelar",
    confirmButtonColor: t.color
  })
  if (!isConfirmed) return

  guardando.value = caso.id
  try {
    await api.patch(`/kommo/pendientes/${caso.id}/estado`, { estado, nota })
    await cargar({ silencioso: true })
    Swal.fire({ icon: "success", title: t.ok, timer: 1400, showConfirmButton: false })
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo cambiar el estado",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
    cargar({ silencioso: true })
  } finally {
    guardando.value = null
  }
}

const escapar = (s) =>
  String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c])

async function verHistorial(caso) {
  try {
    const { data } = await api.get(`/kommo/pendientes/${caso.id}/historial`)
    const filas = data.historial
      .map(
        (h) => `
        <tr>
          <td class="text-nowrap">${escapar(formatFecha(h.creado_at))}</td>
          <td>${escapar(ESTADOS[h.estado_nuevo]?.label || h.estado_nuevo)}</td>
          <td>${escapar(h.usuario || "Automático")}</td>
          <td>${escapar(h.nota || "")}</td>
        </tr>`
      )
      .join("")
    Swal.fire({
      title: "Historial del caso",
      width: 760,
      html: `
        <div class="table-responsive text-start">
          <table class="table table-sm align-middle">
            <thead><tr><th>Fecha</th><th>Estado</th><th>Quién</th><th>Nota</th></tr></thead>
            <tbody>${filas || '<tr><td colspan="4">Sin movimientos</td></tr>'}</tbody>
          </table>
        </div>`
    })
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo cargar el historial",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  }
}
</script>

<template>
  <div class="container-fluid py-4">
    <div class="row g-3 mb-4">
      <div class="col-md-4">
        <div class="card shadow-sm h-100">
          <div class="card-body d-flex align-items-center justify-content-between">
            <div>
              <p class="text-sm text-uppercase font-weight-bold mb-1">Pendientes</p>
              <h3 class="mb-0">{{ conteo.pendiente }}</h3>
              <p class="text-xs text-muted mb-0">Nadie los ha tomado</p>
            </div>
            <div class="icon icon-shape bg-gradient-warning shadow text-center rounded-circle">
              <i class="bi bi-hourglass-split text-lg opacity-10 text-white"></i>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card shadow-sm h-100">
          <div class="card-body d-flex align-items-center justify-content-between">
            <div>
              <p class="text-sm text-uppercase font-weight-bold mb-1">En gestión</p>
              <h3 class="mb-0">{{ conteo.en_gestion }}</h3>
              <p class="text-xs text-muted mb-0">Un asesor los tomó</p>
            </div>
            <div class="icon icon-shape bg-gradient-info shadow text-center rounded-circle">
              <i class="bi bi-person-workspace text-lg opacity-10 text-white"></i>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card shadow-sm h-100">
          <div class="card-body d-flex align-items-center justify-content-between">
            <div>
              <p class="text-sm text-uppercase font-weight-bold mb-1">Atendidos hoy</p>
              <h3 class="mb-0">{{ conteo.atendidos_hoy }}</h3>
              <p class="text-xs text-muted mb-0">Manual o detectado en Kommo</p>
            </div>
            <div class="icon icon-shape bg-gradient-success shadow text-center rounded-circle">
              <i class="bi bi-check2-circle text-lg opacity-10 text-white"></i>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="card-header pb-0">
        <div class="d-flex flex-wrap justify-content-between align-items-start gap-2">
          <div>
            <h5 class="mb-0">
              <i class="bi bi-chat-dots me-2"></i>Clientes sin respuesta
              <span class="badge bg-danger ms-2">{{ totalSinAtender }}</span>
            </h5>
            <p class="text-sm text-muted mb-0">
              Clientes de Kommo que escribieron de último y llevan más de 3 horas sin respuesta. Cuando alguien
              les responde en Kommo, se marcan como atendidos solos.
            </p>
          </div>
          <div class="text-end">
            <span class="text-xs" :class="syncAtrasada ? 'text-danger fw-bold' : 'text-muted'">
              <i class="bi" :class="syncAtrasada ? 'bi-exclamation-triangle' : 'bi-arrow-repeat'"></i>
              {{ ultimaSync ? `Última sincronización con Kommo: hace ${tiempoDesde(ultimaSync)}` : "Aún no hay sincronización con Kommo" }}
            </span>
            <button type="button" class="btn btn-sm btn-outline-dark mb-0 ms-2" :disabled="cargando" @click="cargar({ refrescar: true })">
              <i class="bi bi-arrow-clockwise"></i> Actualizar
            </button>
          </div>
        </div>

        <div class="d-flex flex-wrap gap-2 align-items-center mt-3">
          <div class="btn-group" role="group">
            <button
              v-for="f in FILTROS"
              :key="f.valor"
              type="button"
              class="btn btn-sm mb-0"
              :class="filtro === f.valor ? 'btn-dark' : 'btn-outline-dark'"
              @click="cambiarFiltro(f.valor)"
            >
              {{ f.label }}
            </button>
          </div>
          <input
            v-model="busqueda"
            type="search"
            class="form-control form-control-sm ms-md-auto"
            style="max-width: 280px"
            placeholder="Buscar nombre, teléfono, correo o ID de lead"
            @input="buscar"
          >
        </div>
      </div>

      <div class="card-body">
        <div v-if="cargando" class="text-center py-5">
          <div class="spinner-border text-primary" role="status"></div>
          <p v-if="filtro === 'embudo'" class="text-sm text-muted mt-2 mb-0">
            Consultando los leads en Kommo… la primera vez puede tardar unos segundos.
          </p>
        </div>

        <template v-else-if="filtro === 'embudo'">
          <div v-if="errorEmbudo" class="alert alert-warning text-white mb-0">
            <i class="bi bi-exclamation-triangle me-1"></i>{{ errorEmbudo }}
          </div>
          <template v-else>
            <p class="text-sm text-muted mb-3">
              <b>{{ embudo.leads.length }}</b> leads activos en el embudo
              <b>{{ embudo.pipeline?.nombre || "—" }}</b>, del que lleva más tiempo al más reciente
              (sin contar Ganados ni Perdidos).
              <span v-if="embudo.generadoAt" class="text-xs">Datos de Kommo de hace {{ tiempoDesde(embudo.generadoAt) }}.</span>
            </p>
            <div v-if="!embudoFiltrado.length" class="text-center text-muted py-5">
              <i class="bi bi-inbox fs-2 d-block mb-2"></i>
              {{ busqueda ? "Ningún lead coincide con la búsqueda." : "No hay leads en este embudo." }}
            </div>
            <div v-else class="table-responsive">
              <table class="table align-middle mb-0">
                <thead>
                  <tr>
                    <th class="text-xs text-uppercase">#</th>
                    <th class="text-xs text-uppercase">Lead</th>
                    <th class="text-xs text-uppercase">Contacto</th>
                    <th class="text-xs text-uppercase">Etapa</th>
                    <th class="text-xs text-uppercase">En el embudo</th>
                    <th class="text-xs text-uppercase">Responsable</th>
                    <th class="text-xs text-uppercase text-end">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(lead, i) in embudoFiltrado" :key="lead.lead_id">
                    <td class="text-sm text-muted">{{ i + 1 }}</td>
                    <td>
                      <div class="fw-bold text-sm">{{ lead.lead_nombre }}</div>
                      <div class="text-xs text-muted">Lead #{{ lead.lead_id }}</div>
                      <span v-if="lead.sin_respuesta" class="badge bg-danger mt-1">Sin respuesta</span>
                    </td>
                    <td class="text-sm">
                      <div v-if="lead.contacto_nombre" class="text-xs text-muted">{{ lead.contacto_nombre }}</div>
                      <div v-if="lead.telefono">
                        <i class="bi bi-telephone me-1"></i>{{ lead.telefono }}
                        <a v-if="linkWhatsapp(lead.telefono)" :href="linkWhatsapp(lead.telefono)" target="_blank" rel="noopener" class="ms-1 text-success" title="Abrir en WhatsApp">
                          <i class="bi bi-whatsapp"></i>
                        </a>
                      </div>
                      <div v-if="lead.email" class="text-xs"><i class="bi bi-envelope me-1"></i>{{ lead.email }}</div>
                      <span v-if="!lead.contacto_nombre && !lead.telefono && !lead.email" class="text-muted">—</span>
                    </td>
                    <td class="text-sm">
                      <span class="badge text-dark" :style="{ backgroundColor: lead.etapa_color || '#e9ecef' }">{{ lead.etapa }}</span>
                      <div class="text-xs text-muted mt-1">En esta etapa hace {{ tiempoDesde(lead.etapa_desde_at) }}</div>
                    </td>
                    <td class="text-sm">
                      <div :class="claseTiempoEmbudo(lead.entrada_at)">{{ tiempoDesde(lead.entrada_at) }}</div>
                      <div class="text-xs text-muted">
                        {{ lead.entrada_fuente === "creacion" ? "Creado" : "Entró" }} {{ formatFecha(lead.entrada_at) }}
                      </div>
                    </td>
                    <td class="text-sm">{{ lead.responsable || "—" }}</td>
                    <td class="text-end">
                      <a :href="lead.lead_url" target="_blank" rel="noopener" class="btn btn-sm btn-outline-primary mb-0" title="Abrir en Kommo">
                        <i class="bi bi-box-arrow-up-right"></i> Kommo
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </template>

        <div v-else-if="!casos.length" class="text-center text-muted py-5">
          <i class="bi bi-emoji-smile fs-2 d-block mb-2"></i>
          {{ filtro === "atendido" ? "No hay casos atendidos en los últimos 7 días." : "No hay clientes esperando respuesta." }}
        </div>

        <div v-else class="table-responsive">
          <table class="table align-middle mb-0">
            <thead>
              <tr>
                <th class="text-xs text-uppercase">Cliente</th>
                <th class="text-xs text-uppercase">Contacto</th>
                <th class="text-xs text-uppercase">Canal</th>
                <th class="text-xs text-uppercase">Esperando</th>
                <th class="text-xs text-uppercase">Estado</th>
                <th class="text-xs text-uppercase text-end">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="caso in casos" :key="caso.id">
                <td>
                  <div class="fw-bold text-sm">{{ caso.lead_nombre || `Lead #${caso.lead_id}` }}</div>
                  <div v-if="caso.contacto_nombre" class="text-xs text-muted">{{ caso.contacto_nombre }}</div>
                  <div class="text-xs text-muted">Lead #{{ caso.lead_id }}</div>
                </td>
                <td class="text-sm">
                  <div v-if="caso.telefono">
                    <i class="bi bi-telephone me-1"></i>{{ caso.telefono }}
                    <a v-if="linkWhatsapp(caso.telefono)" :href="linkWhatsapp(caso.telefono)" target="_blank" rel="noopener" class="ms-1 text-success" title="Abrir en WhatsApp">
                      <i class="bi bi-whatsapp"></i>
                    </a>
                  </div>
                  <div v-if="caso.email" class="text-xs"><i class="bi bi-envelope me-1"></i>{{ caso.email }}</div>
                  <span v-if="!caso.telefono && !caso.email" class="text-muted">—</span>
                </td>
                <td class="text-sm">{{ nombreOrigen(caso.origen) }}</td>
                <td class="text-sm">
                  <div :class="claseEspera(caso)">
                    {{ caso.estado === "atendido" ? `${horasEsperando(caso).toFixed(1)} h` : tiempoDesde(caso.cliente_escribio_at) }}
                  </div>
                  <div class="text-xs text-muted">Escribió {{ formatFecha(caso.cliente_escribio_at) }}</div>
                </td>
                <td>
                  <span class="badge" :class="ESTADOS[caso.estado]?.badge">{{ ESTADOS[caso.estado]?.label || caso.estado }}</span>
                  <div v-if="caso.estado === 'atendido'" class="text-xs text-muted mt-1">
                    {{ caso.atendido_auto ? "Respondido en Kommo" : `Por ${caso.gestor_nombre || "—"}` }}
                    · {{ formatFecha(caso.atendido_at) }}
                  </div>
                  <div v-else-if="caso.gestor_nombre" class="text-xs text-muted mt-1">{{ caso.gestor_nombre }}</div>
                </td>
                <td class="text-end text-nowrap">
                  <a v-if="caso.lead_url" :href="caso.lead_url" target="_blank" rel="noopener" class="btn btn-sm btn-outline-primary mb-0 me-1" title="Abrir en Kommo">
                    <i class="bi bi-box-arrow-up-right"></i> Kommo
                  </a>
                  <button v-if="caso.estado === 'pendiente'" type="button" class="btn btn-sm btn-info mb-0 me-1" :disabled="guardando === caso.id" @click="cambiarEstado(caso, 'en_gestion')">
                    Tomar
                  </button>
                  <button v-if="caso.estado !== 'atendido'" type="button" class="btn btn-sm btn-success mb-0 me-1" :disabled="guardando === caso.id" @click="cambiarEstado(caso, 'atendido')">
                    Atendido
                  </button>
                  <button v-if="caso.estado !== 'pendiente'" type="button" class="btn btn-sm btn-outline-warning mb-0 me-1" :disabled="guardando === caso.id" title="Devolver a pendiente" @click="cambiarEstado(caso, 'pendiente')">
                    <i class="bi bi-arrow-counterclockwise"></i>
                  </button>
                  <button type="button" class="btn btn-sm btn-outline-secondary mb-0" title="Historial" @click="verHistorial(caso)">
                    <i class="bi bi-clock-history"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>
